<template>
  <transition name="modal-fade">
    <div v-if="localVisible" class="eqm-overlay">
      <div class="eqm-modal">
        <!-- Header fijo -->
        <div class="eqm-header">
          <div class="eqm-header-left">
            <button class="eqm-back" @click="handleClose">
              <i class="el-icon-arrow-left"></i>
            </button>
            <h5 class="eqm-title">Cotizaciones vencidas</h5>
            <span v-if="quotes.length" class="eqm-count-badge">{{
              quotes.length
            }}</span>
          </div>
          <button class="eqm-close" @click="handleClose">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <!-- Filtros -->
        <div class="eqm-filters">
          <el-input
            v-model="search"
            placeholder="Buscar por cliente o codigo..."
            suffix-icon="el-icon-search"
            size="small"
            clearable
            class="eqm-search"
            @input="onFilterChange"
          />
          <div class="eqm-tabs">
            <button
              v-for="opt in daysOptions"
              :key="opt.value"
              :class="['eqm-tab', { active: filterDays === opt.value }]"
              @click="
                filterDays = opt.value;
                onFilterChange();
              "
            >
              {{ opt.label }}
            </button>
          </div>
          <el-select
            v-model="filterMinAmount"
            placeholder="Monto minimo"
            size="small"
            class="eqm-select"
            @change="onFilterChange"
          >
            <el-option label="Todos" :value="0" />
            <el-option label="+$100" :value="100" />
            <el-option label="+$500" :value="500" />
            <el-option label="+$1000" :value="1000" />
          </el-select>
        </div>

        <!-- Contenido con scroll -->
        <div class="eqm-body">
          <!-- Skeleton -->
          <div v-if="loading" class="eqm-skeleton">
            <div v-for="n in 5" :key="n" class="eqm-skeleton-row">
              <div class="sk-check"></div>
              <div class="sk-line w20"></div>
              <div class="sk-line w30"></div>
              <div class="sk-line w15"></div>
              <div class="sk-line w15"></div>
            </div>
          </div>

          <!-- Tabla -->
          <template v-else-if="quotes.length">
            <div class="eqm-table-header">
              <el-checkbox
                :indeterminate="isIndeterminate"
                :value="allSelected"
                @change="toggleSelectAll"
              />
              <span class="eqm-col-code">Codigo</span>
              <span class="eqm-col-client">Cliente</span>
              <span class="eqm-col-amount">Monto</span>
              <span class="eqm-col-days">Dias vencida</span>
              <span class="eqm-col-actions">Acciones</span>
            </div>

            <div
              v-for="quote in filteredQuotes"
              :key="quote.id"
              :class="[
                'eqm-table-row',
                { 'is-selected': selectedIds.includes(quote.id) }
              ]"
            >
              <el-checkbox
                :value="selectedIds.includes(quote.id)"
                @change="v => toggleSelect(quote.id, v)"
              />
              <a class="eqm-col-code eqm-code-link" @click="goToQuote(quote)">{{
                quote.code
              }}</a>
              <span class="eqm-col-client eqm-client-text">{{
                quote.client_name
              }}</span>
              <span class="eqm-col-amount eqm-amount-text"
                >${{ formatMoney(quote.total) }}</span
              >
              <span class="eqm-col-days">
                <span
                  :class="['eqm-days-badge', daysClass(quote.days_overdue)]"
                >
                  {{ quote.days_overdue }} dias
                </span>
              </span>
              <div class="eqm-col-actions eqm-row-actions">
                <el-tooltip
                  content="Enviar mensaje"
                  placement="top"
                  :open-delay="300"
                >
                  <button
                    class="row-icon-btn row-icon-btn--teal"
                    @click="rowSendMessage(quote)"
                  >
                    <i class="el-icon-chat-dot-round"></i>
                  </button>
                </el-tooltip>
                <el-tooltip
                  content="Marcar no aprobada"
                  placement="top"
                  :open-delay="300"
                >
                  <button
                    class="row-icon-btn row-icon-btn--danger"
                    @click="rowDeny(quote)"
                  >
                    <i class="el-icon-remove-outline"></i>
                  </button>
                </el-tooltip>
              </div>
            </div>

            <div v-if="!filteredQuotes.length" class="eqm-no-results">
              <i class="el-icon-search"></i>
              <span>Sin resultados con los filtros aplicados</span>
            </div>
          </template>

          <!-- Empty -->
          <div v-else class="eqm-empty">
            <i class="el-icon-document-checked"></i>
            <span>No hay cotizaciones vencidas</span>
          </div>
        </div>

        <!-- Barra de acciones masivas fija abajo -->
        <transition name="toolbar-slide">
          <div v-if="selectedIds.length" class="eqm-bulk-bar">
            <span class="eqm-bulk-info">
              {{ selectedIds.length }} cotizacion{{
                selectedIds.length !== 1 ? "es" : ""
              }}
              seleccionada{{ selectedIds.length !== 1 ? "s" : "" }}
            </span>
            <div class="eqm-bulk-actions">
              <el-button
                size="small"
                class="eqm-bulk-btn"
                @click="openBulkMessage"
              >
                <i class="el-icon-chat-dot-round"></i> Enviar mensaje ({{
                  selectedIds.length
                }})
              </el-button>
              <el-button
                size="small"
                class="eqm-bulk-btn-secondary"
                @click="bulkDeny"
              >
                No aprobada ({{ selectedIds.length }})
              </el-button>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
import dashboardService from "../services/dashboardService";
import quoteService from "@/modules/dashboard/sales/pages/quotes/services/quoteService";
import batchActionsMixin from "@/assets/mixins/batchActionsMixin";

export default {
  name: "ExpiredQuotesModal",
  mixins: [batchActionsMixin],
  props: {
    visible: { type: Boolean, default: false },
    alertData: { type: Object, default: null }
  },
  data() {
    return {
      loading: false,
      quotes: [],
      search: "",
      filterDays: 15,
      filterMinAmount: 0,
      selectedIds: [],
      daysOptions: [
        { label: "+15 dias", value: 15 },
        { label: "+30 dias", value: 30 },
        { label: "+60 dias", value: 60 }
      ]
    };
  },
  computed: {
    localVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      }
    },
    filteredQuotes() {
      let list = this.quotes;
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(
          r =>
            (r.code || "").toLowerCase().includes(q) ||
            (r.client_name || "").toLowerCase().includes(q)
        );
      }
      if (this.filterMinAmount > 0) {
        list = list.filter(r => r.total >= this.filterMinAmount);
      }
      return list;
    },
    allSelected() {
      return (
        this.filteredQuotes.length > 0 &&
        this.filteredQuotes.every(q => this.selectedIds.includes(q.id))
      );
    },
    isIndeterminate() {
      const count = this.filteredQuotes.filter(q =>
        this.selectedIds.includes(q.id)
      ).length;
      return count > 0 && count < this.filteredQuotes.length;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadQuotes();
        document.addEventListener("keydown", this.onEscape);
      } else {
        document.removeEventListener("keydown", this.onEscape);
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEscape);
  },
  methods: {
    async loadQuotes() {
      this.loading = true;
      this.selectedIds = [];
      try {
        const res = await dashboardService.getExpiredQuotes({
          params: {
            search: this.search,
            days: this.filterDays || 15,
            min_amount: this.filterMinAmount
          }
        });
        if (res.data && res.data.success) {
          this.quotes = res.data.data || [];
        } else {
          this.quotes = [];
          if (res.data && !res.data.success) {
            throw new Error(res.data.message || "Error al cargar");
          }
        }
      } catch (e) {
        this.quotes = [];
        this.$message.error("Error al cargar las cotizaciones");
      } finally {
        this.loading = false;
      }
    },
    onFilterChange() {
      this.loadQuotes();
    },
    toggleSelectAll(val) {
      if (val) {
        const ids = this.filteredQuotes.map(q => q.id);
        this.selectedIds = [...new Set([...this.selectedIds, ...ids])];
      } else {
        const filteredIds = this.filteredQuotes.map(q => q.id);
        this.selectedIds = this.selectedIds.filter(
          id => !filteredIds.includes(id)
        );
      }
    },
    toggleSelect(id, val) {
      if (val) {
        if (!this.selectedIds.includes(id)) {
          this.selectedIds = [...this.selectedIds, id];
        }
      } else {
        this.selectedIds = this.selectedIds.filter(i => i !== id);
      }
    },
    goToQuote(quote) {
      this.$root.$emit("preview-quote", quote.id);
    },
    rowDeny(quote) {
      this.quotes = this.quotes.filter(q => q.id !== quote.id);
      this.selectedIds = this.selectedIds.filter(id => id !== quote.id);
      this.$message.success(
        `Cotizacion ${quote.code} marcada como no aprobada`
      );
    },
    async bulkDeny() {
      const count = this.selectedIds.length;
      try {
        await this.$confirm(
          `Vas a marcar ${count} cotizacion${
            count !== 1 ? "es" : ""
          } como no aprobada${count !== 1 ? "s" : ""}. Confirmar?`,
          "Confirmar accion",
          {
            confirmButtonText: "Si, continuar",
            cancelButtonText: "Cancelar",
            type: "warning"
          }
        );

        // Usar batch processing para marcar múltiples como rechazadas
        await this.executeBatchAction({
          title: "Marcando cotizaciones como no aprobadas",
          items: this.selectedIds,
          processor: batch =>
            quoteService.updateQuoteStatusBatch(batch, {
              status: "rejected"
            }),
          batchSize: 5,
          onSuccess: () => {
            this.quotes = this.quotes.filter(
              q => !this.selectedIds.includes(q.id)
            );
            this.selectedIds = [];
            this.$message.success(
              `${count} cotizacion${count !== 1 ? "es" : ""} marcada${
                count !== 1 ? "s" : ""
              } como no aprobada${count !== 1 ? "s" : ""}`
            );
            this.$emit("quotes-updated");
          }
        });
      } catch {
        // Cancelado
      }
    },
    openBulkMessage() {
      const recipients = this.filteredQuotes
        .filter(q => this.selectedIds.includes(q.id))
        .map(q => ({
          id: q.client_id,
          name: q.client_name,
          phone: q.client_phone || null,
          email: q.client_email || null,
          quoteCode: q.code,
          quoteMonto: q.total,
          quoteDays: q.days_overdue
        }));
      this.$emit("bulk-message", { recipients, context: "quotes" });
    },
    rowSendMessage(quote) {
      this.$emit("bulk-message", {
        recipients: [
          {
            id: quote.client_id,
            name: quote.client_name,
            phone: quote.client_phone || null,
            email: quote.client_email || null,
            quoteCode: quote.code,
            quoteMonto: quote.total,
            quoteDays: quote.days_overdue
          }
        ],
        context: "quotes"
      });
    },
    daysClass(days) {
      if (days >= 60) return "days-red";
      if (days >= 30) return "days-amber";
      return "days-yellow";
    },
    formatMoney(val) {
      return new Intl.NumberFormat("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val || 0);
    },
    handleClose() {
      this.$emit("update:visible", false);
    },
    onEscape(e) {
      if (e.key === "Escape") this.handleClose();
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3ecfb4;
$border: #e4eaf6;
$bg: #f7f8f9;

.eqm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #fff;
  z-index: 2000;
  display: flex;
}
.eqm-modal {
  background: #fff;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.eqm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 56px;
  background: #fff;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
}
.eqm-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.eqm-back {
  width: 36px;
  height: 36px;
  border: none;
  background: #f0f1f5;
  border-radius: 8px;
  color: $primary;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover {
    background: #e4eaf6;
  }
}
.eqm-title {
  font-size: 16px;
  font-weight: 700;
  color: $primary;
  margin: 0;
}
.eqm-count-badge {
  background: #e24b4a;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}
.eqm-close {
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #909399;
  transition: all 0.15s;
  &:hover {
    background: $bg;
    color: $primary;
  }
}

.eqm-filters {
  display: flex;
  gap: 12px;
  padding: 14px 24px;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: center;
}
.eqm-search {
  flex: 1;
  min-width: 180px;
  max-width: 280px;
}
.eqm-tabs {
  display: flex;
  gap: 0;
  border: 1px solid $border;
  border-radius: 6px;
  overflow: hidden;
}
.eqm-tab {
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  background: #fff;
  border: none;
  cursor: pointer;
  border-right: 1px solid $border;
  transition: all 0.15s;
  &:last-child {
    border-right: none;
  }
  &:hover {
    background: $bg;
  }
  &.active {
    background: $primary;
    color: #fff;
  }
}
.eqm-select {
  width: 130px;
}

.eqm-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.eqm-table-header,
.eqm-table-row {
  ::v-deep .el-checkbox {
    margin: 0;
    padding: 0;
    line-height: 1;
    .el-checkbox__label {
      display: none;
    }
  }
}
.eqm-table-header {
  display: grid;
  grid-template-columns: 32px 120px 1fr 120px 120px 80px;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: #fff;
  border-bottom: 2px solid $border;
  font-size: 11px;
  font-weight: 700;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  position: sticky;
  top: 0;
  z-index: 5;
}
.eqm-table-row {
  display: grid;
  grid-template-columns: 32px 120px 1fr 120px 120px 80px;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  border-bottom: 1px solid $border;
  font-size: 14px;
  min-height: 56px;
  transition: background 0.12s;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: $bg;
  }
  &.is-selected {
    background: #f0f4ff;
  }
}
.eqm-code-text {
  font-weight: 600;
  color: $primary;
  font-size: 14px;
}
.eqm-code-link {
  font-weight: 600;
  color: $primary;
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: $accent;
  }
}
.eqm-client-text {
  color: #303133;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.eqm-amount-text {
  font-weight: 700;
  font-size: 14px;
  color: $primary;
}
.eqm-days-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  &.days-red {
    background: #fff0f0;
    color: #e24b4a;
  }
  &.days-amber {
    background: #fff8e6;
    color: #ba7517;
  }
  &.days-yellow {
    background: #fefce8;
    color: #b45309;
  }
}
.eqm-row-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

// Icon-only action buttons
.row-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e4eaf6;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s;
  flex-shrink: 0;

  &--teal {
    color: $accent;
    &:hover {
      background: rgba($accent, 0.08);
      border-color: $accent;
    }
  }
  &--danger {
    color: #909399;
    &:hover {
      background: #fff0f0;
      border-color: #e24b4a;
      color: #e24b4a;
    }
  }
}
.eqm-no-results {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 30px;
  color: #c0c4cc;
  font-size: 12px;
  justify-content: center;
}
.eqm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #c0c4cc;
  gap: 10px;
  i {
    font-size: 36px;
    color: $accent;
  }
  span {
    font-size: 13px;
    font-weight: 600;
    color: #909399;
  }
}

// Barra de acciones masivas fija abajo
.eqm-bulk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #fff;
  border-top: 1px solid $border;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 10px;
}
.eqm-bulk-info {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
}
.eqm-bulk-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.eqm-bulk-btn {
  font-size: 12px;
  background: $accent;
  border: 1px solid $accent;
  color: #fff;
  border-radius: 8px;
  &:hover,
  &:focus {
    background: darken($accent, 5%);
    border-color: darken($accent, 5%);
    color: #fff;
  }
}
.eqm-bulk-btn-secondary {
  font-size: 12px;
  background: #fff;
  border: 1px solid #dcdfe6;
  color: #606266;
  border-radius: 8px;
  &:hover,
  &:focus {
    background: $bg;
    border-color: #c0c4cc;
  }
}

// Transitions
.toolbar-slide-enter-active,
.toolbar-slide-leave-active {
  transition: max-height 0.25s, opacity 0.25s;
  overflow: hidden;
  max-height: 80px;
}
.toolbar-slide-enter,
.toolbar-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s;
  .eqm-modal {
    transition: transform 0.25s;
  }
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
  .eqm-modal {
    transform: translateY(20px);
  }
}

// Skeleton
.eqm-skeleton {
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.eqm-skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sk-check {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  flex-shrink: 0;
}
.sk-line {
  height: 12px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  &.w15 {
    width: 15%;
  }
  &.w20 {
    width: 20%;
  }
  &.w30 {
    width: 30%;
  }
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 768px) {
  .eqm-table-header {
    display: none !important;
  }
  .eqm-table-row {
    display: flex !important;
    flex-wrap: wrap;
    gap: 6px 12px;
    padding: 16px;
    font-size: 14px;
    > .el-checkbox {
      margin-right: 8px;
      flex-shrink: 0;
      align-self: flex-start;
    }
  }

  .eqm-col-actions {
    display: flex !important;
    margin-top: 8px;
    gap: 8px;

    .el-button {
      flex: 1;
      padding: 10px 8px !important;
      font-size: 13px !important;
      min-height: 40px;
    }
  }
}
</style>
